/**
 * Filename: postal-code.ts
 * Description: Secure AWS DynamoDB API postal code client engine for Shin Q admin web console.
 * All requests are routed through HTTPS encrypted API backend with rate-limiting & WAF security. Zero local DB storage.
 * Created by: Teddy Gil "Shin" Ravis Orbeta
 * Created: 2026-07-28
 * Updated: 2026-08-22
 */

export interface PostalCodeAddress {
  postalCode: string;
  prefecture: string;
  city: string;
  street: string;
  country: string;
  prefectureJp: string;
  cityJp: string;
  streetJp: string;
  prefectureEn: string;
  cityEn: string;
  streetEn: string;
}

export interface FullPostalEntry {
  zip: string;
  prefJp: string;
  cityJp: string;
  streetJp: string;
  prefEn: string;
  cityEn: string;
  streetEn: string;
}

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.dev.shinq21.com';
const WEB_APP_BASE = typeof window === 'undefined' ? 'https://admin.dev.shinq21.com' : window.location.origin;

const IN_MEMORY_CACHE: Record<string, PostalCodeAddress> = {};

const HALF_TO_FULL_KANA: Record<string, string> = {
  'ｶﾞ': 'ガ', 'ｷﾞ': 'ギ', 'ｸﾞ': 'グ', 'ｹﾞ': 'ゲ', 'ｺﾞ': 'ゴ',
  'ｻﾞ': 'ザ', 'ｼﾞ': 'ジ', 'ｽﾞ': 'ズ', 'ｾﾞ': 'ゼ', 'ｿﾞ': 'ゾ',
  'ﾀﾞ': 'ダ', 'ﾁﾞ': 'ヂ', 'ﾂﾞ': 'ヅ', 'ﾃﾞ': 'デ', 'ﾄﾞ': 'ド',
  'ﾊﾞ': 'バ', 'ﾋﾞ': 'ビ', 'ﾌﾞ': 'ブ', 'ﾍﾞ': 'ベ', 'ﾎﾞ': 'ボ',
  'ﾊﾟ': 'パ', 'ﾋﾟ': 'ピ', 'ﾌﾟ': 'プ', 'ﾍﾟ': 'ペ', 'ﾎﾟ': 'ポ',
  'ｱ': 'ア', 'ｲ': 'イ', 'ｳ': 'ウ', 'ｴ': 'エ', 'ｵ': 'オ',
  'ｶ': 'カ', 'ｷ': 'キ', 'ｸ': 'ク', 'ｹ': 'ケ', 'ｺ': 'コ',
  'ｻ': 'サ', 'ｼ': 'シ', 'ｽ': 'ス', 'ｾ': 'セ', 'ｿ': 'ソ',
  'ﾀ': 'タ', 'ﾁ': 'チ', 'ﾂ': 'ツ', 'ﾃ': 'テ', 'ﾄ': 'ト',
  'ﾅ': 'ナ', 'ﾆ': 'ニ', 'ﾇ': 'ヌ', 'ﾈ': 'ネ', 'ﾉ': 'ノ',
  'ﾊ': 'ハ', 'ﾋ': 'ヒ', 'ﾌ': 'フ', 'ﾍ': 'ヘ', 'ﾎ': 'ホ',
  'ﾏ': 'マ', 'ﾐ': 'ミ', 'ﾑ': 'ム', 'ﾒ': 'メ', 'ﾓ': 'モ',
  'ﾔ': 'ヤ', 'ﾕ': 'ユ', 'ﾖ': 'ヨ',
  'ﾗ': 'ラ', 'ﾘ': 'リ', 'ﾙ': 'ル', 'ﾚ': 'レ', 'ﾛ': 'ロ',
  'ﾜ': 'ワ', 'ｦ': 'ヲ', 'ﾝ': 'ン',
  'ｧ': 'ァ', 'ｨ': 'ィ', 'ｩ': 'ゥ', 'ｪ': 'ェ', 'ｫ': 'ォ',
  'ｯ': 'ッ', 'ｬ': 'ャ', 'ｭ': 'ュ', 'ｮ': 'ョ',
  'ｰ': 'ー', '･': '・',
};

export function toFullWidthKatakana(input: string): string {
  let str = input;
  for (const [half, full] of Object.entries(HALF_TO_FULL_KANA)) {
    str = str.split(half).join(full);
  }
  return str;
}

export function toHalfWidthNumbers(input: string): string {
  return input.replace(/[０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0));
}

export function getOrdinalSuffix(num: number): string {
  const mod100 = num % 100;
  if (mod100 >= 11 && mod100 <= 13) {
    return `${num}th`;
  }
  const mod10 = num % 10;
  if (mod10 === 1) return `${num}st`;
  if (mod10 === 2) return `${num}nd`;
  if (mod10 === 3) return `${num}rd`;
  return `${num}th`;
}

const LANDMARK_KANJI_MAP: Array<[RegExp, string]> = [
  [/日本橋日本橋高島屋三井ビルディング|ニホンバシニホンバシタカシマヤミツイビルディング/g, 'Nihonbashi Nihonbashi Takashimaya Mitsui Building'],
  [/日本橋高島屋三井ビルディング|ニホンバシタカシマヤミツイビルディング/g, 'Nihonbashi Takashimaya Mitsui Building'],
  [/高島屋三井ビルディング/g, 'Takashimaya Mitsui Building'],
  [/赤坂赤坂アークヒルズ・アーク森ビル|アカサカアカサカアークヒルズ・アークモリビル/g, 'Akasaka Akasaka Ark Hills Ark Mori Bldg'],
  [/赤坂アークヒルズ・アーク森ビル/g, 'Akasaka Ark Hills Ark Mori Bldg'],
  [/アークヒルズ・アーク森ビル/g, 'Ark Hills Ark Mori Bldg'],
  [/アークヒルズ/g, 'Ark Hills'],
  [/アーク森ビル/g, 'Ark Mori Bldg'],
  [/赤坂ミッドタウン・タワー|アカサカミッドタウン・タワー/g, 'Akasaka Midtown Tower'],
  [/ミッドタウン・タワー/g, 'Midtown Tower'],
  [/永田町山王パークタワー|ナガタチョウサンノウパークタワー/g, 'Nagatacho Sanno Park Tower'],
  [/山王パークタワー|サンノウパークタワー/g, 'Sanno Park Tower'],
  [/丸の内パシフィックセンチュリープレイス丸の内|マルノウチパシフィックセンチュリープレイスマルノウチ/g, 'Marunouchi Pacific Century Place Marunouchi'],
  [/パシフィックセンチュリープレイス/g, 'Pacific Century Place'],
  [/東京ポートシティ竹芝オフィスタワー|トウキョウポートシティタケシバオフィスタワー/g, 'Tokyo Port City Takeshiba Office Tower'],
  [/ポートシティ竹芝オフィスタワー/g, 'Port City Takeshiba Office Tower'],
  [/霞が関霞が関ビル|カスミガセキカスミガセキビル/g, 'Kasumigaseki Kasumigaseki Bldg'],
  [/霞が関ビル|カスミガセキビル/g, 'Kasumigaseki Bldg'],
  [/六本木ヒルズタワー|ロッポンギヒルズタワー/g, 'Roppongi Hills Tower'],
  [/日本橋高島屋/g, 'Nihonbashi Takashimaya'],
  [/高島屋/g, 'Takashimaya '],
  [/三井/g, 'Mitsui '],
  [/三菱/g, 'Mitsubishi '],
  [/住友/g, 'Sumitomo '],
  [/野村/g, 'Nomura '],
  [/お花茶屋/g, 'Ohanajaya'],
  [/三軒茶屋/g, 'Sangenjaya'],
  [/高茶屋小森上野町/g, 'Takachaya Komorienocho'],
  [/高茶屋小森町/g, 'Takachaya Komoricho'],
  [/高茶屋/g, 'Takachaya'],
  [/新所・岡崎・梅田入会地/g, 'Shinjo Okazaki Umeda Iriaichi'],
  [/千万町町/g, 'Zenzacho'],
  [/蛇ケ谷/g, 'Jagadani'],
  [/蛇渕/g, 'Jabuchi'],
  [/真光寺ヴィレッジ/g, 'Shinkoji Village'],
  [/ヴィソン/g, 'Vison'],
  [/丸の内/g, 'Marunouchi '],
  [/六本木/g, 'Roppongi '],
  [/銀座/g, 'Ginza '],
  [/新宿/g, 'Shinjuku '],
  [/渋谷/g, 'Shibuya '],
  [/池袋/g, 'Ikebukuro '],
  [/品川/g, 'Shinagawa '],
  [/大手町/g, 'Otemachi '],
  [/日本橋/g, 'Nihonbashi '],
  [/赤坂/g, 'Akasaka '],
  [/恵比寿/g, 'Ebisu '],
  [/目黒/g, 'Meguro '],
  [/原宿/g, 'Harajuku '],
  [/表参道/g, 'Omotesando '],
  [/上野/g, 'Ueno '],
  [/秋葉原/g, 'Akihabara '],
  [/汐留/g, 'Shiodome '],
  [/有楽町/g, 'Yurakucho '],
  [/虎ノ門/g, 'Toranomon '],
  [/麻布/g, 'Azabu '],
  [/竹芝/g, 'Takeshiba '],
  [/海岸/g, 'Kaigan '],
];

const BUILDING_TERM_MAP: Array<[RegExp, string]> = [
  [/パークビルディング|パ−クビルディング|パ-クビルディング/gi, 'Park Building'],
  [/パークビル|パ−クビル|パ-クビル/gi, 'Park Bldg'],
  [/パークタワー|パ−クタワ−|パ-クタワ-/gi, 'Park Tower'],
  [/オフィスタワー|オフィスタワ−|オフィスタワ-/gi, 'Office Tower'],
  [/グランドタワー|グランドタワ−|グランドタワ-/gi, 'Grand Tower'],
  [/シティタワー|シティタワ−|シティタワ-/gi, 'City Tower'],
  [/ヒルズタワー|ヒルズタワ−|ヒルズタワ-/gi, 'Hills Tower'],
  [/ビルディング/gi, 'Building'],
  [/ビル/gi, 'Bldg'],
  [/タワー|タワ−|タワ-/gi, 'Tower'],
  [/マンション/gi, 'Mansion'],
  [/アパート|アパ−ト|アパ-ト/gi, 'Apartment'],
  [/ハイツ/gi, 'Heights'],
  [/コーポ|コ−ポ|コ-ポ/gi, 'Corpo'],
  [/レジデンス/gi, 'Residence'],
  [/スクエア/gi, 'Square'],
  [/ガーデン|ガ−デン|ガ-デン/gi, 'Garden'],
  [/プラザ/gi, 'Plaza'],
  [/ヒルズ/gi, 'Hills'],
  [/ヴィラ|ビラ/gi, 'Villa'],
  [/ハウス/gi, 'House'],
  [/コート|コ−ト|コ-ト/gi, 'Court'],
  [/パレス/gi, 'Palace'],
  [/ステージ|ステ−ジ|ステ-ジ/gi, 'Stage'],
  [/グランド/gi, 'Grand'],
  [/テラス/gi, 'Terrace'],
  [/シティ/gi, 'City'],
  [/センチュリー/gi, 'Century'],
  [/フォレスト/gi, 'Forest'],
  [/パーク|パ−ク|パ-ク/gi, 'Park'],
  [/ポート/gi, 'Port'],
  [/オフィス/gi, 'Office'],
  [/センター|センタ−|センタ-/gi, 'Center'],
  [/ステーション/gi, 'Station'],
  [/ゲート/gi, 'Gate'],
  [/アベニュー/gi, 'Avenue'],
  [/ストリート/gi, 'Street'],
  [/メゾン/gi, 'Maison'],
  [/ロイヤル/gi, 'Royal'],
  [/グリーン/gi, 'Green'],
  [/ウエスト/gi, 'West'],
  [/イースト/gi, 'East'],
  [/サウス/gi, 'South'],
  [/ノース/gi, 'North'],
  [/セントラル/gi, 'Central'],
  [/パシフィック/gi, 'Pacific'],
  [/プレイス/gi, 'Place'],
];

export function translateJapaneseFloorAndRoom(input: string): string {
  if (!input) return '';
  let str = toHalfWidthNumbers(input.trim());

  // Translate landmark names (e.g. 六本木, 丸の内, 銀座)
  for (const [regex, replacement] of LANDMARK_KANJI_MAP) {
    str = str.replace(regex, replacement);
  }

  // Translate building terms (e.g. タワー -> Tower, パークビルディング -> Park Building, ビル -> Bldg)
  for (const [regex, replacement] of BUILDING_TERM_MAP) {
    str = str.replace(regex, replacement);
  }

  // Translate Basement floors (e.g. 地下1階, B1階, 地下１階 -> Basement 1st floor)
  str = str.replace(/(?:地下|B)(\d+)階?/gi, (_, numStr) => {
    const num = parseInt(numStr, 10);
    return `Basement ${getOrdinalSuffix(num)} floor`;
  });

  // Translate standard floors (e.g. 1階, ２階, 21階, 3F -> 1st floor, 2nd floor, 21st floor)
  str = str.replace(/(\d+)\s*(?:階|F|f)/g, (_, numStr) => {
    const num = parseInt(numStr, 10);
    return `${getOrdinalSuffix(num)} floor`;
  });

  // Translate room numbers (e.g. 101号室, 202室 -> Room 101, Room 202)
  str = str.replace(/(\d+)\s*(?:号室|室|号)/g, (_, numStr) => {
    return `Room ${numStr}`;
  });

  return str.replace(/\s+/g, ' ').trim();
}

export function containsJapanese(input: string): boolean {
  return /[\u3040-\u309f\u30a0-\u30ff\u4e00-\u9faf\uff61-\uff9f]/u.test(input);
}

export function isValidCountry(input: string): boolean {
  if (!input || !input.trim()) return false;
  return input.trim().length >= 2;
}

export function formatEnglishCityName(cityJp: string, cityEn: string): string {
  if (!cityEn) return '';
  let str = cityEn;

  if (cityJp === '八丈島八丈町' || /Hachijojimahachijomachi/i.test(str)) {
    return 'Hachijojima Hachijomachi';
  }
  if (cityJp === '三宅島三宅村' || /Miyakejimamiyakemura/i.test(str)) {
    return 'Miyakejima Miyakemura';
  }
  if (cityJp === '神津島村' || /Kouzushimamura|Kojishimamura/i.test(str)) {
    return 'Kouzushima Mura';
  }
  if (cityJp === '青ヶ島村' || /Aogashimamura/i.test(str)) {
    return 'Aogashima Mura';
  }
  if (cityJp === '御蔵島村' || /Mikurajimamura/i.test(str)) {
    return 'Mikurajima Mura';
  }
  if (cityJp === '利島村' || /Toshimamura/i.test(str)) {
    return 'Toshima Mura';
  }
  if (cityJp === '小笠原村' || /Ogasawaramura/i.test(str)) {
    return 'Ogasawara Mura';
  }
  if (cityJp === '大島町' || /Oshimamachi/i.test(str)) {
    return 'Oshima Machi';
  }

  str = str.replace(/(jima|shima)(hachijo|machi|cho|son|mura|shi)/gi, '$1 $2');
  str = str.replace(/([a-zA-Z]+)gun([a-zA-Z]+)/g, (m, p1, p2) => {
    return p1.charAt(0).toUpperCase() + p1.slice(1) + '-gun ' + p2.charAt(0).toUpperCase() + p2.slice(1);
  });
  str = str.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  return str;
}

export async function lookupJapanesePostalCode(rawCode: string, isJapanese: boolean = false): Promise<PostalCodeAddress | null> {
  const cleanZip = rawCode.replace(/[^\d]/g, '');
  if (cleanZip.length < 3) return null;

  const cacheKey = `${cleanZip}_${isJapanese ? 'jp' : 'en'}`;
  if (IN_MEMORY_CACHE[cacheKey]) {
    return IN_MEMORY_CACHE[cacheKey];
  }

  try {
    const response = await fetch(`${API_BASE}/v1/postal-code?code=${cleanZip}&lang=${isJapanese ? 'jp' : 'en'}`);
    if (response.ok) {
      const data = await response.json();
      IN_MEMORY_CACHE[cacheKey] = data;
      return data;
    }
  } catch (err) {
    console.warn('Secure AWS DynamoDB API Postal Lookup Error:', err);
  }

  // Fallback to S3 CDN if API Gateway is unavailable
  try {
    const prefix2 = cleanZip.slice(0, 2);
    const res = await fetch(`${WEB_APP_BASE}/postal/${prefix2}.json`);
    if (res.ok) {
      const chunk = await res.json();
      const entry = chunk[cleanZip];
      if (entry) {
        const formattedZip = cleanZip.length >= 7 ? `${cleanZip.slice(0, 3)}-${cleanZip.slice(3)}` : cleanZip;
        const country = isJapanese ? '日本' : 'Japan';
        const formattedCityEn = formatEnglishCityName(entry.cityJp || '', entry.cityEn || '');
        const fallbackObj = {
          postalCode: formattedZip,
          prefecture: isJapanese ? entry.prefJp : entry.prefEn,
          city: isJapanese ? entry.cityJp : formattedCityEn,
          street: isJapanese ? entry.streetJp : entry.streetEn,
          country,
          prefectureJp: entry.prefJp,
          cityJp: entry.cityJp,
          streetJp: entry.streetJp,
          prefectureEn: entry.prefEn,
          cityEn: formattedCityEn,
          streetEn: entry.streetEn,
        };
        IN_MEMORY_CACHE[cacheKey] = fallbackObj;
        return fallbackObj;
      }
    }
  } catch (fallbackErr) {
    console.warn('AWS CDN fallback error:', fallbackErr);
  }

  return null;
}
